import React from 'react';
import { useTranslation } from 'react-i18next';
import Cropper from 'react-easy-crop';

import { EmployeeAvatar, Spinner } from '../../../shared/components';
import { listCompanies } from '../../companies/services/companyService';
import { formatDateTime } from '../../../shared/utils/localization';
import { getCurrentEmployee, getMyAiProfile, removeMyProfileImage, updateMyAiProfile, uploadMyProfileImage } from '../services/employeeService';
import type { Employee, EmployeeAiProfile, UpdateEmployeeAiProfileRequest } from '../types/Employee';
import { getCroppedImageFile } from './profileImageCropUtils';

import styles from './MyProfilePage.module.css';

const ACCEPTED_PROFILE_IMAGE_TYPES = 'image/png,image/jpeg,image/gif,image/webp';
type CropState = {
  file: File;
  previewUrl: string;
  crop: { x: number; y: number };
  zoom: number;
  croppedAreaPixels: { x: number; y: number; width: number; height: number } | null;
};

const toAiProfileDraft = (profile?: EmployeeAiProfile | null): UpdateEmployeeAiProfileRequest => ({
  aboutMeForAi: profile?.aboutMeForAi ?? '',
  responsePreferences: profile?.responsePreferences ?? '',
  expertiseAreas: profile?.expertiseAreas ?? '',
  publicTeamSummary: profile?.publicTeamSummary ?? '',
});

const normalizeDraftValue = (value?: string) => (value || '').trim();
const formatEmployeePhone = (employee: Employee) => [employee.phoneCountryCode, employee.phoneNumber].filter(Boolean).join(' ') || '-';

const getCharacterCountClassName = (value: string | undefined, maxLength: number, stylesMap: Record<string, string>) => {
  const count = (value ?? '').length;

  if (count >= maxLength) {
    return `${stylesMap.aiFieldCounter} ${stylesMap.aiFieldCounterLimit}`;
  }

  if (count >= Math.floor(maxLength * 0.85)) {
    return `${stylesMap.aiFieldCounter} ${stylesMap.aiFieldCounterWarning}`;
  }

  return stylesMap.aiFieldCounter;
};

export const MyProfilePage: React.FC = () => {
  const { t } = useTranslation(['admin', 'common', 'header']);
  const [employee, setEmployee] = React.useState<Employee | null>(null);
  const [aiProfile, setAiProfile] = React.useState<EmployeeAiProfile | null>(null);
  const [aiProfileDraft, setAiProfileDraft] = React.useState<UpdateEmployeeAiProfileRequest>(() => toAiProfileDraft(null));
  const [companyName, setCompanyName] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [uploading, setUploading] = React.useState(false);
  const [removing, setRemoving] = React.useState(false);
  const [savingAiProfile, setSavingAiProfile] = React.useState(false);
  const [showAvatarSuccess, setShowAvatarSuccess] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [aiProfileError, setAiProfileError] = React.useState<string | null>(null);
  const [aiProfileSavedAt, setAiProfileSavedAt] = React.useState<string | null>(null);
  const [photoMenuOpen, setPhotoMenuOpen] = React.useState(false);
  const [cropState, setCropState] = React.useState<CropState | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);
  const avatarMenuRef = React.useRef<HTMLDivElement | null>(null);
  const successTimeoutRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        window.clearTimeout(successTimeoutRef.current);
      }

      if (cropState?.previewUrl) {
        URL.revokeObjectURL(cropState.previewUrl);
      }
    };
  }, [cropState?.previewUrl]);

  React.useEffect(() => {
    if (!photoMenuOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!avatarMenuRef.current?.contains(event.target as Node)) {
        setPhotoMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [photoMenuOpen]);

  const pulseAvatarSuccess = React.useCallback(() => {
    setShowAvatarSuccess(true);
    if (successTimeoutRef.current) {
      window.clearTimeout(successTimeoutRef.current);
    }

    successTimeoutRef.current = window.setTimeout(() => {
      setShowAvatarSuccess(false);
      successTimeoutRef.current = null;
    }, 1800);
  }, []);

  const loadProfile = React.useCallback(async () => {
    setLoading(true);
    try {
      const [response, profile] = await Promise.all([
        getCurrentEmployee(),
        getMyAiProfile(),
      ]);

      if (!response) {
        setEmployee(null);
        setAiProfile(null);
        setAiProfileDraft(toAiProfileDraft(null));
        setCompanyName('');
        setError(t('failedToLoadProfile', { ns: 'admin' }));
        return;
      }

      setEmployee(response.employee);
      setAiProfile(profile);
      setAiProfileDraft(toAiProfileDraft(profile));
      setAiProfileSavedAt(null);
      setAiProfileError(null);

      try {
        const companies = await listCompanies({ page: 1, pageSize: 1 });
        setCompanyName(companies[0]?.name?.trim() || 'Company');
      } catch {
        setCompanyName('Company');
      }

      setError(null);
    } catch {
      setError(t('failedToLoadProfile', { ns: 'admin' }));
    } finally {
      setLoading(false);
    }
  }, [t]);

  React.useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  const emitProfileUpdated = React.useCallback((nextEmployee: Employee) => {
    window.dispatchEvent(new CustomEvent('employee:profile-updated', { detail: nextEmployee }));
  }, []);

  const onPickPhoto = () => {
    setPhotoMenuOpen(false);
    fileInputRef.current?.click();
  };

  const onFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return;
    }

    setError(null);

    const previewUrl = URL.createObjectURL(file);
    setCropState({
      file,
      previewUrl,
      crop: { x: 0, y: 0 },
      zoom: 1,
      croppedAreaPixels: null,
    });
  };

  const closeCropper = React.useCallback(() => {
    setCropState((current) => {
      if (current?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      return null;
    });
  }, []);

  const onCropComplete = React.useCallback((_: unknown, croppedAreaPixels: { x: number; y: number; width: number; height: number }) => {
    setCropState((current) => (current ? { ...current, croppedAreaPixels } : current));
  }, []);

  const confirmCropUpload = React.useCallback(async () => {
    if (!cropState?.croppedAreaPixels) {
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const croppedFile = await getCroppedImageFile(
        cropState.previewUrl,
        cropState.croppedAreaPixels,
        cropState.file.name,
        cropState.file.type
      );

      const updated = await uploadMyProfileImage(croppedFile);
      if (!updated) {
        throw new Error('Missing updated employee payload');
      }

      setEmployee(updated);
      emitProfileUpdated(updated);
      pulseAvatarSuccess();
      closeCropper();
    } catch {
      setError(t('failedToUploadPhoto', { ns: 'admin' }));
    } finally {
      setUploading(false);
    }
  }, [closeCropper, cropState, emitProfileUpdated, pulseAvatarSuccess, t]);

  const onRemovePhoto = async () => {
    setPhotoMenuOpen(false);
    setRemoving(true);
    setError(null);

    try {
      const updated = await removeMyProfileImage();
      if (!updated) {
        throw new Error('Missing updated employee payload');
      }

      setEmployee(updated);
      emitProfileUpdated(updated);
      pulseAvatarSuccess();
    } catch {
      setError(t('failedToRemovePhoto', { ns: 'admin' }));
    } finally {
      setRemoving(false);
    }
  };

  const updateAiProfileField = React.useCallback((field: keyof UpdateEmployeeAiProfileRequest, value: string) => {
    setAiProfileDraft((current) => ({
      ...current,
      [field]: value,
    }));
    setAiProfileError(null);
    setAiProfileSavedAt(null);
  }, []);

  const saveAiProfile = React.useCallback(async () => {
    setSavingAiProfile(true);
    setAiProfileError(null);

    try {
      const saved = await updateMyAiProfile({
        aboutMeForAi: aiProfileDraft.aboutMeForAi ?? '',
        responsePreferences: aiProfileDraft.responsePreferences ?? '',
        expertiseAreas: aiProfileDraft.expertiseAreas ?? '',
        publicTeamSummary: aiProfileDraft.publicTeamSummary ?? '',
      });

      if (!saved) {
        throw new Error('Missing AI profile payload');
      }

      setAiProfile(saved);
      setAiProfileDraft(toAiProfileDraft(saved));
      setAiProfileSavedAt(saved.updatedAt ?? new Date().toISOString());
    } catch {
      setAiProfileError(t('failedToSaveAiProfile', { ns: 'admin' }));
    } finally {
      setSavingAiProfile(false);
    }
  }, [aiProfileDraft, t]);

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <Spinner text={t('loadingEmployee', { ns: 'admin' })} />
      </div>
    );
  }

  if (!employee) {
    return <div className={`error ${styles.errorBanner}`}>{error || t('failedToLoadProfile', { ns: 'admin' })}</div>;
  }

  const resolvedCompanyName = companyName || 'Company';
  const isAiProfileDirty =
    normalizeDraftValue(aiProfileDraft.aboutMeForAi) !== normalizeDraftValue(aiProfile?.aboutMeForAi) ||
    normalizeDraftValue(aiProfileDraft.responsePreferences) !== normalizeDraftValue(aiProfile?.responsePreferences) ||
    normalizeDraftValue(aiProfileDraft.expertiseAreas) !== normalizeDraftValue(aiProfile?.expertiseAreas) ||
    normalizeDraftValue(aiProfileDraft.publicTeamSummary) !== normalizeDraftValue(aiProfile?.publicTeamSummary);
  const aiProfileUpdatedAt = aiProfileSavedAt || aiProfile?.updatedAt;

  return (
    <div className={styles.page}>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_PROFILE_IMAGE_TYPES}
        className={styles.hiddenInput}
        onChange={onFileSelected}
      />

      <section className={styles.heroCard}>
        <div className={styles.heroGlow} aria-hidden />
        <div className={styles.heroContent}>
          <div className={styles.identityColumn}>
            <div className={styles.avatarStack}>
              <div className={styles.avatarMenuWrap} ref={avatarMenuRef}>
                <EmployeeAvatar
                  name={employee.name}
                  imageUrl={employee.profileImageUrl}
                  colorPreference={employee.colorPreference}
                  className={styles.heroAvatar}
                  size={128}
                  isUploading={uploading || removing}
                  showSuccess={showAvatarSuccess}
                />
                <button
                  className={styles.avatarCueButton}
                  type="button"
                  onClick={() => setPhotoMenuOpen((current) => !current)}
                  disabled={uploading || removing}
                  aria-haspopup="menu"
                  aria-expanded={photoMenuOpen}
                  aria-label={t('profilePhotoActions', { ns: 'admin' })}
                >
                  <svg className={styles.avatarCueIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                    <path d="M9 6.75A4.25 4.25 0 0 1 13.25 11H14l.42-.42a2.75 2.75 0 0 1 1.95-.8h1.88A2.75 2.75 0 0 1 21 12.53v4.72A2.75 2.75 0 0 1 18.25 20H6.75A2.75 2.75 0 0 1 4 17.25v-4.5A2.75 2.75 0 0 1 6.75 10H7.5A4.25 4.25 0 0 1 9 6.75Zm0 1.5A2.75 2.75 0 0 0 6.25 11v.5h-1A1.25 1.25 0 0 0 4 12.75v4.5c0 .69.56 1.25 1.25 1.25h11.5c.69 0 1.25-.56 1.25-1.25v-4.72c0-.69-.56-1.25-1.25-1.25h-1.88c-.33 0-.65.13-.88.37l-.86.85H11.5V11A2.75 2.75 0 0 0 9 8.25Zm7.25 4.75v1.75H18v1.5h-1.75V18h-1.5v-1.75H13v-1.5h1.75V13h1.5Zm-7.75 1a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5Zm0 1.5a1 1 0 1 0 0 2 1 1 0 0 0 0-2Z" fill="currentColor" />
                  </svg>
                </button>
                {photoMenuOpen ? (
                  <div className={styles.photoMenu} role="menu">
                    <button className={styles.photoMenuItem} type="button" onClick={onPickPhoto} disabled={uploading || removing}>
                      {employee.profileImageUrl ? t('replacePhoto', { ns: 'admin' }) : t('uploadPhoto', { ns: 'admin' })}
                    </button>
                    <button className={styles.photoMenuItem} type="button" onClick={() => void onRemovePhoto()} disabled={!employee.profileImageUrl || uploading || removing}>
                      {t('removePhoto', { ns: 'admin' })}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
            <div className={styles.identityCopy}>
              <span className={styles.eyebrow}>{t('myProfile', { ns: 'admin' })}</span>
              <h1 className={styles.title}>{employee.name || t('myProfile', { ns: 'admin' })}</h1>
              <p className={styles.subtitle}>{t('profileSubtitle', { ns: 'admin' })}</p>
              <div className={styles.badgeRow}>
                <span className={styles.badge}>{resolvedCompanyName}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {error ? <div className={`error ${styles.errorBanner}`}>{error}</div> : null}

      {cropState ? (
        <div className={styles.cropOverlay} role="dialog" aria-modal="true" aria-label={t('profilePhoto', { ns: 'admin' })}>
          <div className={styles.cropCard}>
            <div className={styles.cropHeader}>
              <div>
                <h2>{t('profilePhoto', { ns: 'admin' })}</h2>
                <p>{t('photoHint', { ns: 'admin' })}</p>
              </div>
              <button className={styles.cropCloseButton} type="button" onClick={closeCropper} disabled={uploading}>
                ×
              </button>
            </div>

            <div className={styles.cropAreaWrap}>
              <Cropper
                image={cropState.previewUrl}
                crop={cropState.crop}
                zoom={cropState.zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={(crop) => setCropState((current) => (current ? { ...current, crop } : current))}
                onZoomChange={(zoom) => setCropState((current) => (current ? { ...current, zoom } : current))}
                onCropComplete={onCropComplete}
              />
            </div>

            <div className={styles.cropSliderRow}>
              <span>{t('common:zoom', { defaultValue: 'Zoom' })}</span>
              <input
                className={styles.cropSlider}
                type="range"
                min={1}
                max={3}
                step={0.05}
                value={cropState.zoom}
                onChange={(event) => setCropState((current) => (current ? { ...current, zoom: Number(event.target.value) } : current))}
              />
            </div>

            <div className={styles.cropActions}>
              <button className="btn btn-secondary" type="button" onClick={closeCropper} disabled={uploading}>
                {t('common:cancel', { defaultValue: 'Cancel' })}
              </button>
              <button className="btn btn-primary" type="button" onClick={() => void confirmCropUpload()} disabled={uploading}>
                {uploading ? t('uploadingPhoto', { ns: 'admin' }) : t('common:save', { defaultValue: 'Save' })}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <section className={styles.detailsGrid}>
        <article className={styles.infoCard}>
          <span className={styles.infoLabel}>{t('memberEmail', { ns: 'admin' })}</span>
          <strong>{employee.email || '-'}</strong>
        </article>
        <article className={styles.infoCard}>
          <span className={styles.infoLabel}>{t('title', { ns: 'admin' })}</span>
          <strong>{employee.title || '-'}</strong>
        </article>
        <article className={styles.infoCard}>
          <span className={styles.infoLabel}>{t('memberSince', { ns: 'admin' })}</span>
          <strong>{employee.createdAt ? formatDateTime(employee.createdAt) : '-'}</strong>
        </article>
        <article className={styles.infoCard}>
          <span className={styles.infoLabel}>{t('tenantContext', { ns: 'admin' })}</span>
          <strong>{resolvedCompanyName}</strong>
        </article>
        <article className={styles.infoCard}>
          <span className={styles.infoLabel}>{t('phoneNumber', { ns: 'admin' })}</span>
          <strong>{formatEmployeePhone(employee)}</strong>
        </article>
        <article className={styles.infoCard}>
          <span className={styles.infoLabel}>{t('language', { ns: 'admin' })}</span>
          <strong>{employee.language || '-'}</strong>
        </article>
        <article className={styles.infoCard}>
          <span className={styles.infoLabel}>{t('department', { ns: 'admin' })}</span>
          <strong>{employee.departmentName || '-'}</strong>
        </article>
        <article className={styles.infoCard}>
          <span className={styles.infoLabel}>{t('position', { ns: 'admin' })}</span>
          <strong>{employee.positionName || '-'}</strong>
        </article>
      </section>

      <section className={styles.aiSection}>
        <div className={styles.aiSectionCopy}>
          <span className={styles.aiSectionEyebrow}>{t('myProfile', { ns: 'admin' })}</span>
          <h2>{t('profileWorkspaceTitle', { ns: 'admin' })}</h2>
          <p>{t('profileWorkspaceDescription', { ns: 'admin' })}</p>
        </div>

        <section className={styles.aiProfileCard}>
              <div className={styles.aiProfileHeader}>
                <div>
                  <span className={styles.aiProfileEyebrow}>{t('aiProfile', { ns: 'admin' })}</span>
                  <h2>{t('aiProfileTitle', { ns: 'admin' })}</h2>
                  <p>{t('aiProfileDescription', { ns: 'admin' })}</p>
                </div>
                <div className={styles.aiProfileMeta}>
                  <span className={styles.aiProfileMetaBadge}>{t('aiProfilePrivateBadge', { ns: 'admin' })}</span>
                  {aiProfileUpdatedAt ? <span>{t('aiProfileLastUpdated', { ns: 'admin', date: formatDateTime(aiProfileUpdatedAt) })}</span> : null}
                </div>
              </div>

              <div className={styles.aiProfileGrid}>
                <label className={styles.aiField}>
                  <span>{t('aiProfileAboutMeLabel', { ns: 'admin' })}</span>
                  <small className={styles.aiFieldMeta}>{t('aiProfilePrivateFieldHint', { ns: 'admin' })}</small>
                  <textarea
                    className={styles.aiTextarea}
                    value={aiProfileDraft.aboutMeForAi ?? ''}
                    onChange={(event) => updateAiProfileField('aboutMeForAi', event.target.value)}
                    placeholder={t('aiProfileAboutMePlaceholder', { ns: 'admin' })}
                    maxLength={2000}
                    rows={5}
                  />
                  <span className={getCharacterCountClassName(aiProfileDraft.aboutMeForAi, 2000, styles)}>
                    {(aiProfileDraft.aboutMeForAi ?? '').length}/2000
                  </span>
                </label>

                <label className={styles.aiField}>
                  <span>{t('aiProfileResponsePreferencesLabel', { ns: 'admin' })}</span>
                  <small className={styles.aiFieldMeta}>{t('aiProfilePrivateFieldHint', { ns: 'admin' })}</small>
                  <textarea
                    className={styles.aiTextarea}
                    value={aiProfileDraft.responsePreferences ?? ''}
                    onChange={(event) => updateAiProfileField('responsePreferences', event.target.value)}
                    placeholder={t('aiProfileResponsePreferencesPlaceholder', { ns: 'admin' })}
                    maxLength={1000}
                    rows={4}
                  />
                  <span className={getCharacterCountClassName(aiProfileDraft.responsePreferences, 1000, styles)}>
                    {(aiProfileDraft.responsePreferences ?? '').length}/1000
                  </span>
                </label>

                <label className={styles.aiField}>
                  <span>{t('aiProfileExpertiseLabel', { ns: 'admin' })}</span>
                  <small className={styles.aiFieldMeta}>{t('aiProfilePrivateFieldHint', { ns: 'admin' })}</small>
                  <textarea
                    className={styles.aiTextarea}
                    value={aiProfileDraft.expertiseAreas ?? ''}
                    onChange={(event) => updateAiProfileField('expertiseAreas', event.target.value)}
                    placeholder={t('aiProfileExpertisePlaceholder', { ns: 'admin' })}
                    maxLength={1000}
                    rows={4}
                  />
                  <span className={getCharacterCountClassName(aiProfileDraft.expertiseAreas, 1000, styles)}>
                    {(aiProfileDraft.expertiseAreas ?? '').length}/1000
                  </span>
                </label>

                <label className={styles.aiField}>
                  <span>{t('aiProfilePublicSummaryLabel', { ns: 'admin' })}</span>
                  <small className={`${styles.aiFieldMeta} ${styles.aiFieldMetaPublic}`}>{t('aiProfilePublicFieldHint', { ns: 'admin' })}</small>
                  <textarea
                    className={styles.aiTextarea}
                    value={aiProfileDraft.publicTeamSummary ?? ''}
                    onChange={(event) => updateAiProfileField('publicTeamSummary', event.target.value)}
                    placeholder={t('aiProfilePublicSummaryPlaceholder', { ns: 'admin' })}
                    maxLength={1000}
                    rows={4}
                  />
                  <span className={getCharacterCountClassName(aiProfileDraft.publicTeamSummary, 1000, styles)}>
                    {(aiProfileDraft.publicTeamSummary ?? '').length}/1000
                  </span>
                </label>
              </div>

              <div className={styles.aiProfileFooter}>
                <p className={styles.aiProfileHint}>{t('aiProfileHint', { ns: 'admin' })}</p>
                <div className={styles.aiProfileActions}>
                  {aiProfileError ? <div className={`error ${styles.aiProfileError}`}>{aiProfileError}</div> : null}
                  <button
                    className="btn btn-secondary"
                    type="button"
                    onClick={() => {
                      setAiProfileDraft(toAiProfileDraft(aiProfile));
                      setAiProfileError(null);
                      setAiProfileSavedAt(null);
                    }}
                    disabled={!isAiProfileDirty || savingAiProfile}
                  >
                    {t('reset', { ns: 'admin' })}
                  </button>
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={() => void saveAiProfile()}
                    disabled={!isAiProfileDirty || savingAiProfile}
                  >
                    {savingAiProfile ? t('savingAiProfile', { ns: 'admin' }) : t('saveAiProfile', { ns: 'admin' })}
                  </button>
                </div>
              </div>
        </section>
      </section>
    </div>
  );
};

export default MyProfilePage;